import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useRwas from "../../../hooks/useRwas";
import useCompany from "../../../hooks/useCompany";
import toast from "react-hot-toast";
import useImage from "../../../hooks/useImage";
import useMultimedia from "../../../hooks/useMultimedia";
import Toggle from "../../../components/Toggle.jsx";

export default function RWACreate({ rwaId }) {
  const { activeCompany: company } = useCompany();
  const { type } = useParams();
  const navigate = useNavigate();
  const { convertToBlob } = useImage();
  const { createRWA, updateRwa } = useRwas();
  const { upload } = useMultimedia();
  const [isClaimable, setIsClaimable] = useState(false);
  const [isBuyable, setIsBuyable] = useState(false);

  // dynamically import the component based on the type
  const Component = useMemo(
    () => React.lazy(() => import(`./create/${type}.jsx`)),
    [type]
  );

  const handleCreate = async ({name, params}) => {
    if (!name) return toast.error(`Name is required`);

    const formatedParams = {};
    let imageFile = null;
    let imageKey = null;

    for (const [key, value] of Object.entries(params)) {
      if (value.type === "image" && value.value) {
        imageFile = value.value;
        imageKey = key;
        formatedParams[key] = {
          type: value.type,
          value: ''
        };
        continue;
      }
      if (value.type === "longtext") {
        formatedParams[key] = {
          type: value.type,
          value: value.value || ''
        };
        continue;
      }
      formatedParams[key] = value;
    }
    console.log("Params:", formatedParams);

    try {
      const newRwa = await createRWA(company.id, {
        companyId: company.id,
        type,
        name,
        isClaimable,
        isBuyable,
        params: formatedParams
      });

      if (imageFile && imageKey) {
        let fileToUpload = imageFile;
        // Si no es File o Blob, conviértelo
        if (!(imageFile instanceof File) && !(imageFile instanceof Blob)) {
          const { blob, contentType } = await convertToBlob(imageFile);
          // Crea un File para mantener el nombre y el tipo
          fileToUpload = new File([blob], "image.png", { type: contentType });
        }
        // Sube el archivo directamente, el hook se encarga del FormData
        const fileUrl = await upload(fileToUpload);
        await updateRwa(company.id, newRwa.id, {
          params: {
            [imageKey]: { value: fileUrl, type: "image" }
          }
        });
      }

      toast.success(`${type} created successfully`);
      navigate(`/dashboard/rwa/${newRwa?.id}`);
    } catch (error) {
      console.error(error);
      toast.error(`Error creating ${type}`);
    }
  };

  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <div className="px-6 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex flex-col">
            <span className="text-slate-900 text-sm font-medium">Claimable</span>
            <span className="text-slate-500 text-xs">Users can claim this asset</span>
          </div>
          <Toggle checked={isClaimable} onChange={setIsClaimable} text="Claimable" />
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex flex-col">
            <span className="text-slate-900 text-sm font-medium">Buyable</span>
            <span className="text-slate-500 text-xs">Users can buy this asset</span>
          </div>
          <Toggle checked={isBuyable} onChange={setIsBuyable} text="Buyable" />
        </div>
      </div>
      <Component handleCreate={handleCreate} />
    </React.Suspense>
  );
}